import React, { useEffect, useState } from "react";
import DataTable from "../../components/common/DataTable";
import { Button, Card } from "react-bootstrap";
import { formatToUSDate } from "../../utils/dateFormat";
import { useSelector } from "react-redux";
import MemberModal from "../../components/member/MemberModal";
import { useNavigate } from "react-router-dom";
import ConfirmDeleteModal from "../../components/common/ConfirmDeleteModal";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";

function RegisterMembership() {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [plan, setPlan] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);

  const { user } = useSelector((state) => state.USER_DATA);
  const navigate = useNavigate();

  const customPrimaryRow = {
    _id: "primary-custom-row",
    first_name: `${user?.first_name || ""} ${user?.last_name || ""}`,
    date_of_birth: formatToUSDate(user?.date_of_birth?.split("T")[0]),
    gender: user?.gender
      ? user.gender.charAt(0).toUpperCase() + user.gender.slice(1)
      : "",
    marital_status: user?.marital_status || "",
    relationship: "Primary",
  };

  const fetchMembers = async () => {
    try {
      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      if (response.status === 200) {
        setMembers(response?.data?.payload?.result || []);
      }
    } catch (error) {
      console.error("Error fetching members:", error);
    }
  };

  const fetchPlanAmount = async () => {
    try {
      const response = await API.get(api_paths.member.membership.GET_PLAN);

      if (response.status === 200) {
        const amount = response.data?.payload?.result || 0;

        if (Array.isArray(amount) && amount.length > 0) {
          setPlan(amount[0]); // store first plan object
        }
      }
    } catch (error) {
      console.error("Error fetching plan amount:", error);
    }
  };

  const membershipColumns = [
    {
      key: "first_name",
      label: "Name",
      render: (_, row) => `${row.first_name || ""} ${row.last_name || ""}`,
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => formatToUSDate(val?.split("T")[0]),
    },
    { key: "gender", label: "Gender" },
    { key: "marital_status", label: "Marital Status" },
    { key: "relationship", label: "Relationship" },
  ];

  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);

      await Promise.all([fetchMembers(), fetchPlanAmount()]);

      setPageLoading(false);
    };

    loadData();
  }, []);

  const handleDelete = async (row) => {
    setSelectedRow(row);
    setShowDeleteModal(true);
  };
  const handleDeleteConfirmed = async (row) => {
    if (!row?._id || row.relationship === "Primary") return;

    try {
      setLoading(true);
      await API.delete(
        `${api_paths.member.family_member.DELETE_MEMBER}/${row._id}`
      );

      const response = await API.get(api_paths.member.family_member.GET_MEMBER);

      setMembers(response.data.payload.result || []);
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete member. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "70vh" }}
      >
        <div className="spinner-border text-orange" role="status"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="container py-5">
        <div className="row mb-2">
          <div className="col-lg-8">
            <h1 className="inter-bold member-ship-title">
              Manage your membership and Family Members
            </h1>
          </div>
          <div className="col-lg-4 d-flex justify-content-lg-end align-items-center mb-2">
            <button
              className="btn btn-lg btn-orange"
              onClick={() => {
                setEditData(null);
                setShowModal(true);
              }}
            >
              Add Member
            </button>
            <button
              type="submit"
              className="btn btn-lg btn-black px-4 ms-4"
              onClick={() => navigate("/profile")}
            >
              Dashboard
            </button>
          </div>
        </div>
        <div className="row mt-2">
          <div className="col-lg-8 mb-4 mb-lg-0">
            <DataTable
              columns={membershipColumns}
              data={[customPrimaryRow, ...members]}
              loading={loading}
              onEdit={(row) => {
                if (row.relationship !== "Primary") {
                  setEditData(row);
                  setShowModal(true);
                }
              }}
              onDelete={handleDelete}
            />
          </div>
          <div className="col-lg-4">
            <Card className="fees-card p-4">
              <h5 className="fw-semibold fs-16 text-main mb-0">
                Membership Fees
              </h5>

              {/* Annual Plan Name */}
              <div className="mt-1 plan-title">
                <strong>{plan?.plan_name}</strong>
              </div>

              {/* Primary Member */}
              <div className="d-flex justify-content-between mt-3 fees-content-1">
                <div>
                  <div>Primary Member 1 × ${plan?.amount}</div>
                  {plan.discount_amount && (
                    <div className="text-muted small">
                      (${plan?.discount_amount} discount applied)
                    </div>
                  )}
                </div>
                <strong>${plan?.amount}</strong>
              </div>

              {/* Family Members */}
              {members.length > 0 && (
                <div className="d-flex justify-content-between mt-3  fees-content-1">
                  <div>
                    <div>
                      Family Member(s) {members.length} × ${plan?.amount}
                    </div>
                    {plan.discount_amount && (
                      <div className="text-muted small">
                        (${plan?.discount_amount * members.length} discount
                        applied)
                      </div>
                    )}
                  </div>
                  <strong>${plan?.amount * members.length}</strong>
                </div>
              )}

              <hr />

              {/* Total Discount */}
              {plan.discount_amount && (
                <div className="d-flex justify-content-between">
                  <strong>Total Discount</strong>
                  <strong>
                    -$
                    {plan?.discount_amount +
                      plan?.discount_amount * members.length}
                  </strong>
                </div>
              )}
              <hr />
              {/* Total Amount */}
              <div className="d-flex justify-content-between total-amount mt-2">
                <p className="fw-semibold mb-0">Total Amount</p>
                <p className="fw-semibold mb-0">
                  $
                  {plan?.amount +
                    plan?.amount * members.length -
                    (plan?.discount_amount +
                      plan?.discount_amount * members.length)}
                </p>
              </div>

              <Button className="btn-orange-lg w-100 mt-3">
                Process to Pay
              </Button>
            </Card>
          </div>
        </div>
      </div>

      <MemberModal
        show={showModal}
        onHide={() => setShowModal(false)}
        editData={editData}
        refreshList={fetchMembers}
      />

      <ConfirmDeleteModal
        show={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={() => {
          handleDeleteConfirmed(selectedRow);
          setShowDeleteModal(false);
        }}
        message={
          <>
            Are you sure you want to delete{" "}
            <strong>{selectedRow?.first_name}</strong>?
          </>
        }
      />
    </div>
  );
}

export default RegisterMembership;
